import React, { useState } from 'react';
import {
    Modal,
    Form,
    Input,
    Select,
    Upload,
    message,
    Alert,
} from 'antd';
import { InboxOutlined } from '@ant-design/icons';
import {
    useCreateTicketMutation,
    useGetTicketCategoriesQuery,
    useGetBrandsQuery,
    useUploadTicketAttachmentMutation,
} from '../../services/nestjs-base-api';
import { useAuth } from '../../hooks/useAuth';
import { useIntl } from 'react-intl';

const { TextArea } = Input;
const { Option } = Select;
const { Dragger } = Upload;

interface CreateTicketModalProps {
    visible: boolean;
    onClose: () => void;
    onSuccess?: (ticketHash: string) => void;
}

const CreateTicketModal: React.FC<CreateTicketModalProps> = ({ visible, onClose, onSuccess }) => {
    const { user } = useAuth();
    const intl = useIntl();
    const [form] = Form.useForm();
    const [fileList, setFileList] = useState<any[]>([]);
    const [selectedCategoryId, setSelectedCategoryId] = useState<number | null>(null);

    const [createTicket, { isLoading: isCreating }] = useCreateTicketMutation();
    const [uploadAttachment] = useUploadTicketAttachmentMutation();
    const { data: categories } = useGetTicketCategoriesQuery({ jwtToken: user?.jwtToken || '' });
    const { data: brandsData } = useGetBrandsQuery({
        jwtToken: user?.jwtToken || '',
        organisationId: user?.organisationId || 0,
    });

    const handleSubmit = async (values: any) => {
        try {
            const result = await createTicket({
                jwtToken: user?.jwtToken || '',
                data: {
                    subject: values.subject,
                    description: values.description,
                    categoryId: values.categoryId,
                    brandId: values.brandId,
                    priority: values.priority || 'normal',
                },
            }).unwrap();

            // Upload attachments if any
            if (fileList.length > 0) {
                const uploadPromises = fileList.map((fileItem) =>
                    uploadAttachment({
                        jwtToken: user?.jwtToken || '',
                        hash: result.hash,
                        file: fileItem.originFileObj,
                    })
                );

                try {
                    await Promise.all(uploadPromises);
                } catch (uploadError) {
                    message.warning(intl.formatMessage({ id: 'support.create.attachmentWarning' }));
                }
            }

            message.success(intl.formatMessage({ id: 'support.create.success' }));
            form.resetFields();
            setFileList([]);

            if (onSuccess) {
                onSuccess(result.hash);
            } else {
                onClose();
            }
        } catch (error: any) {
            message.error(error?.data?.message || intl.formatMessage({ id: 'support.create.error' }));
        }
    };

    const handleCancel = () => {
        form.resetFields();
        setFileList([]);
        setSelectedCategoryId(null);
        onClose();
    };

    // Get dynamic placeholder based on selected category
    const getDescriptionPlaceholder = () => {
        if (!selectedCategoryId || !categories) {
            return intl.formatMessage({ id: 'support.create.description.placeholder' });
        }

        const selectedCategory = categories.find(cat => cat.id === selectedCategoryId);
        if (!selectedCategory) {
            return intl.formatMessage({ id: 'support.create.description.placeholder' });
        }

        // Category-specific instructions
        const categoryInstructions: { [key: string]: string } = {
            'Technical Issue': 'Please describe the technical issue in detail:\n\n1. What were you trying to do?\n2. What happened instead?\n3. Steps to reproduce the issue\n4. Browser and device information\n5. Error messages or screenshots (attach below if available)',
            'Feature Request': 'Please describe the feature you\'d like to see:\n\n1. What problem would this feature solve?\n2. How would you like it to work?\n3. Any examples from other tools?\n4. How would this benefit your workflow?',
            'Billing & Account': 'Please provide details about your billing or account inquiry:\n\n1. Type of request (billing question, upgrade/downgrade, invoice copy, payment issue, account access, etc.)\n2. Your organization name\n3. Invoice number or billing period (if applicable)\n4. Detailed description of your request\n5. Any relevant payment or subscription information',
            'General Question': 'Please describe your question or concern in detail.\n\nInclude any relevant context that will help us assist you better, such as:\n- What you\'re trying to accomplish\n- Any related features or data\n- Screenshots if helpful (attach below)',
            'Other': 'Please describe your support request in detail.\n\nInclude:\n- What you need help with\n- Any relevant context\n- Steps you\'ve already tried\n- Screenshots if applicable (attach below)',
        };

        return categoryInstructions[selectedCategory.name] || intl.formatMessage({ id: 'support.create.description.placeholder' });
    };

    return (
        <Modal
            title={intl.formatMessage({ id: 'support.create.title' })}
            open={visible}
            onOk={() => form.submit()}
            onCancel={handleCancel}
            confirmLoading={isCreating}
            width={600}
            okText={intl.formatMessage({ id: 'support.create.button' })}
        >
            <Alert
                message={intl.formatMessage({ id: 'support.create.alert.title' })}
                description={intl.formatMessage({ id: 'support.create.alert.description' })}
                type="info"
                showIcon
                style={{ marginBottom: 24 }}
            />

            <Form
                form={form}
                layout="vertical"
                onFinish={handleSubmit}
            >
                <Form.Item
                    label={intl.formatMessage({ id: 'support.create.subject.label' })}
                    name="subject"
                    rules={[
                        { required: true, message: intl.formatMessage({ id: 'support.create.subject.required' }) },
                        { min: 5, message: intl.formatMessage({ id: 'support.create.subject.minLength' }) },
                        { max: 255, message: intl.formatMessage({ id: 'support.create.subject.maxLength' }) },
                    ]}
                >
                    <Input
                        placeholder={intl.formatMessage({ id: 'support.create.subject.placeholder' })}
                        maxLength={255}
                        showCount
                    />
                </Form.Item>

                <Form.Item
                    label={intl.formatMessage({ id: 'support.create.category.label' })}
                    name="categoryId"
                    rules={[{ required: true, message: intl.formatMessage({ id: 'support.create.category.required' }) }]}
                >
                    <Select
                        placeholder={intl.formatMessage({ id: 'support.create.category.placeholder' })}
                        onChange={(value) => setSelectedCategoryId(value)}
                    >
                        {categories?.map((cat) => (
                            <Option key={cat.id} value={cat.id}>
                                {cat.name}
                            </Option>
                        ))}
                    </Select>
                </Form.Item>

                <Form.Item
                    label={intl.formatMessage({ id: 'support.create.description.label' })}
                    name="description"
                    rules={[
                        { required: true, message: intl.formatMessage({ id: 'support.create.description.required' }) },
                        { min: 20, message: intl.formatMessage({ id: 'support.create.description.minLength' }) },
                    ]}
                >
                    <TextArea
                        rows={8}
                        placeholder={getDescriptionPlaceholder()}
                        maxLength={5000}
                        showCount
                    />
                </Form.Item>

                <Form.Item
                    label={intl.formatMessage({ id: 'support.create.brand.label' })}
                    name="brandId"
                >
                    <Select placeholder={intl.formatMessage({ id: 'support.create.brand.placeholder' })} allowClear>
                        {brandsData?.map((brand) => (
                            <Option key={brand.id} value={brand.id}>
                                {brand.name}
                            </Option>
                        ))}
                    </Select>
                </Form.Item>

                <Form.Item
                    label={intl.formatMessage({ id: 'support.create.priority.label' })}
                    name="priority"
                    initialValue="normal"
                >
                    <Select>
                        <Option value="low">{intl.formatMessage({ id: 'support.create.priority.low' })}</Option>
                        <Option value="normal">{intl.formatMessage({ id: 'support.create.priority.normal' })}</Option>
                        <Option value="high">{intl.formatMessage({ id: 'support.create.priority.high' })}</Option>
                        <Option value="urgent">{intl.formatMessage({ id: 'support.create.priority.urgent' })}</Option>
                    </Select>
                </Form.Item>

                <Form.Item label={intl.formatMessage({ id: 'support.create.attachments.label' })}>
                    <Dragger
                        fileList={fileList}
                        beforeUpload={() => false}
                        onChange={({ fileList }) => setFileList(fileList)}
                        multiple
                        maxCount={5}
                    >
                        <p className="ant-upload-drag-icon">
                            <InboxOutlined />
                        </p>
                        <p className="ant-upload-text">
                            {intl.formatMessage({ id: 'support.create.attachments.dragText' })}
                        </p>
                        <p className="ant-upload-hint">
                            {intl.formatMessage({ id: 'support.create.attachments.hint' })}
                        </p>
                    </Dragger>
                </Form.Item>
            </Form>
        </Modal>
    );
};

export default CreateTicketModal;
